import { GIFEncoder, quantize, applyPalette } from 'gifenc';

const BAYER_4X4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
];

function applyBayerDitherRgbInPlace(rgba, width, height, bayerScale) {
  const strength = 8 * bayerScale;
  for (let y = 0; y < height; y += 1) {
    const y3 = y & 3;
    for (let x = 0; x < width; x += 1) {
      const t = (BAYER_4X4[y3][x & 3] + 0.5) / 16 - 0.5;
      const d = t * strength;
      const i = (y * width + x) * 4;
      rgba[i] = Math.max(0, Math.min(255, Math.round(rgba[i] + d)));
      rgba[i + 1] = Math.max(0, Math.min(255, Math.round(rgba[i + 1] + d)));
      rgba[i + 2] = Math.max(0, Math.min(255, Math.round(rgba[i + 2] + d)));
    }
  }
}

/** Encode same-size canvas frames as a looping GIF. */
export function encodeGifFromCanvases(canvases, delayMs, { dither = true } = {}) {
  if (!canvases.length) throw new Error('No frames');
  const w = canvases[0].width;
  const h = canvases[0].height;
  const mega = new Uint8Array(w * h * 4 * canvases.length);
  let offset = 0;
  for (let i = 0; i < canvases.length; i += 1) {
    const c = canvases[i];
    if (c.width !== w || c.height !== h) {
      throw new Error('GIF frames must share dimensions');
    }
    const d = c.getContext('2d').getImageData(0, 0, w, h).data;
    mega.set(d, offset);
    offset += d.length;
  }
  const palette = quantize(mega, 256);
  const gif = GIFEncoder();
  canvases.forEach((canvas, i) => {
    const rgba = canvas.getContext('2d').getImageData(0, 0, w, h).data;
    if (dither) {
      applyBayerDitherRgbInPlace(rgba, w, h, 3);
    }
    const index = applyPalette(rgba, palette);
    if (i === 0) {
      gif.writeFrame(index, w, h, {
        palette,
        delay: delayMs,
        repeat: 0,
      });
    } else {
      gif.writeFrame(index, w, h, { delay: delayMs });
    }
  });
  gif.finish();
  return gif.bytes();
}

export function dataUrlToCanvas(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = img.width;
      c.height = img.height;
      c.getContext('2d').drawImage(img, 0, 0);
      resolve(c);
    };
    img.onerror = () => reject(new Error('Failed to decode frame'));
    img.src = dataUrl;
  });
}

export function waitAnimationFrames(count) {
  return new Promise((resolve) => {
    let n = 0;
    function tick() {
      n += 1;
      if (n >= count) resolve();
      else requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  });
}
